import React, { useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { usePortfolio } from "../context/PortfolioContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { fmtINR, fmtUnits, longDate } from "../lib/format";
import { Loader2, Pencil, Trash2, X, Sparkles, Layers, TrendingUp, Percent } from "lucide-react";
import StatCard from "../components/StatCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { stampedName } from "../lib/csv";
import ExportButtons from "../components/ExportButtons";

export default function Bonus() {
  const { user } = useAuth();
  const { transactions, members, summary, loading, reload } = usePortfolio();
  const { toast } = useToast();
  const [memberId, setMemberId] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";
  const currentNAV = summary?.currentNAV || 0;

  const bonusTxs = useMemo(
    () => transactions.filter((t) => t.type === "bonus"),
    [transactions]
  );

  const filtered = useMemo(() => {
    return bonusTxs
      .filter((c) => (memberId === "all" ? true : c.memberId === memberId))
      .filter((c) => {
        if (fromDate && c.date < fromDate) return false;
        if (toDate && c.date > toDate) return false;
        return true;
      })
      .slice()
      .sort((a, b) => {
        const da = new Date(a.date);
        const db = new Date(b.date);
        if (db - da !== 0) return db - da;
        return (b.id || "").localeCompare(a.id || "");
      });
  }, [bonusTxs, memberId, fromDate, toDate]);

  const totals = filtered.reduce(
    (acc, c) => {
      const funds = c.fundsCr || 0;
      const units = c.unitsCr || 0;
      acc.bonusCapital += funds;
      acc.bonusUnits += units;
      return acc;
    },
    { bonusCapital: 0, bonusUnits: 0 }
  );
  const bonusCurrentValue = totals.bonusUnits * currentNAV;
  const bonusGain = bonusCurrentValue - totals.bonusCapital;
  const bonusGainPct =
    totals.bonusCapital > 0 ? (bonusGain / totals.bonusCapital) * 100 : 0;

  const exportData = useMemo(() => {
    const headers = ["Date", "Member", "Bonus NAV", "Bonus Units", "Bonus Capital", "Current NAV", "Current Value", "Bonus Gain", "Bonus Gain %", "Details"];
    const rows = filtered.map((c) => {
      const units = c.unitsCr || 0;
      const capital = c.fundsCr || 0;
      const curVal = units * currentNAV;
      const gain = curVal - capital;
      const gainPct = capital > 0 ? (gain / capital) * 100 : 0;
      return [
        c.date, c.name, c.nav, units, capital, currentNAV, curVal, gain,
        gainPct.toFixed(2), c.details || "",
      ];
    });
    const pdfColumns = ["Date", "Member", "NAV", "Units", "Capital", "Current", "Gain", "Gain %"];
    const pdfRows = filtered.map((c) => {
      const units = c.unitsCr || 0;
      const capital = c.fundsCr || 0;
      const curVal = units * currentNAV;
      const gain = curVal - capital;
      const gainPct = capital > 0 ? (gain / capital) * 100 : 0;
      return [
        c.date, c.name,
        "₹" + c.nav.toFixed(4),
        fmtUnits(units),
        fmtINR(capital),
        fmtINR(curVal),
        fmtINR(gain),
        gainPct.toFixed(2) + "%",
      ];
    });
    return { headers, rows, pdfColumns, pdfRows };
  }, [filtered, currentNAV]);

  if (loading)
    return (
      <AppShell>
        <div className="flex items-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      </AppShell>
    );

  const clearFilters = () => {
    setMemberId("all");
    setFromDate("");
    setToDate("");
  };
  const filtersActive = memberId !== "all" || fromDate || toDate;

  const updateEdit = (k, v) => {
    setEditing((e) => {
      const next = { ...e, [k]: v };
      const funds = parseFloat(next.funds);
      const nav = parseFloat(next.nav);
      if (!isNaN(funds) && !isNaN(nav) && nav > 0 && (k === "funds" || k === "nav")) {
        next.units = (funds / nav).toFixed(4);
      }
      return next;
    });
  };

  const startEdit = (t) => {
    setEditing({
      id: t.id,
      memberName: t.name,
      date: t.date,
      type: "bonus",
      details: t.details || "",
      funds: String(t.fundsCr),
      units: String(t.unitsCr),
      nav: t.nav.toFixed(4),
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const funds = parseFloat(editing.funds);
    const nav = parseFloat(editing.nav);
    const units = parseFloat(editing.units);
    if (isNaN(funds) || funds <= 0) return toast({ title: "Invalid amount" });
    if (isNaN(nav) || nav <= 0) return toast({ title: "Invalid NAV" });
    if (isNaN(units) || units <= 0) return toast({ title: "Invalid units" });
    setBusy(true);
    try {
      await api.editTransaction(editing.id, {
        date: editing.date,
        type: "bonus",
        details: editing.details,
        funds,
        nav,
        units,
      });
      toast({ title: "Bonus updated" });
      setEditing(null);
      await reload();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Could not update" });
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.deleteTransaction(deleting.id);
      toast({ title: "Bonus deleted" });
      setDeleting(null);
      await reload();
    } catch (err) {
      toast({ title: "Failed", description: err.response?.data?.detail || "Could not delete" });
    }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] text-amber-300/90 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6, background: "#fbbf24", boxShadow: "0 0 12px rgba(251,191,36,0.7)" }} />
            <Sparkles size={11} /> Bonus
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">Bonus Issuances</h1>
          <div className="text-xs text-slate-400 mt-1 font-mono">
            Free units issued to members — tracked separately from cash subscriptions/redemptions.
          </div>
        </div>
        <ExportButtons
          testIdPrefix="bonus-export"
          disabled={filtered.length === 0}
          csv={{
            filename: stampedName("bonus"),
            headers: exportData.headers,
            rows: exportData.rows,
          }}
          pdf={{
            title: "Bonus issuances",
            subtitle: `${filtered.length} records · Total ${fmtINR(totals.bonusCapital)}`,
            filename: "bonus",
            orientation: "landscape",
            columns: exportData.pdfColumns,
            rows: exportData.pdfRows,
            summary: [
              ["Bonus capital issued", fmtINR(totals.bonusCapital)],
              ["Bonus units issued", fmtUnits(totals.bonusUnits)],
              ["Current value", fmtINR(bonusCurrentValue)],
              ["Bonus gain", `${fmtINR(bonusGain)} (${bonusGainPct.toFixed(2)}%)`],
            ],
          }}
        />
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          testId="bonus-capital-total"
          icon={Sparkles}
          label="Bonus Capital Issued"
          value={fmtINR(totals.bonusCapital, { compact: true })}
          sub="Notional value"
          tone="amber"
          valueClassName="text-amber-400"
        />
        <StatCard
          testId="bonus-units-total"
          icon={Layers}
          label="Bonus Units Issued"
          value={fmtUnits(totals.bonusUnits)}
          sub="Free units allotted"
          tone="amber"
        />
        <StatCard
          testId="bonus-current-value"
          icon={TrendingUp}
          label="Current Value"
          value={fmtINR(bonusCurrentValue, { compact: true })}
          sub={`@ NAV ₹${currentNAV.toFixed(4)}`}
        />
        <StatCard
          testId="bonus-gain"
          icon={Percent}
          label="Bonus Gain"
          value={fmtINR(bonusGain, { compact: true })}
          sub={`${bonusGain >= 0 ? "+" : ""}${bonusGainPct.toFixed(2)}%`}
          positive={bonusGain >= 0}
          tone={bonusGain >= 0 ? "emerald" : "rose"}
          valueClassName={bonusGain >= 0 ? "text-emerald-400" : "text-rose-400"}
        />
      </div>

      {/* Filters row */}
      <div className="mt-5 bg-slate-900 border border-slate-800 rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[180px]">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Member</div>
            <Select value={memberId} onValueChange={setMemberId}>
              <SelectTrigger data-testid="bonus-member-filter" className="bg-slate-950 border-slate-800 text-white h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-white max-h-[320px]">
                <SelectItem value="all">All members</SelectItem>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">From</div>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="bonus-from-date"
              className="bg-slate-950 border-slate-800 text-white h-9 w-[150px]"
            />
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">To</div>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="bonus-to-date"
              className="bg-slate-950 border-slate-800 text-white h-9 w-[150px]"
            />
          </div>
          {filtersActive && (
            <button
              onClick={clearFilters}
              data-testid="bonus-clear-filters"
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1 h-9 px-3"
            >
              <X size={12} /> Clear
            </button>
          )}
          <div className="ml-auto text-xs text-slate-500">
            {filtered.length} of {bonusTxs.length} records
          </div>
        </div>
      </div>

      <div className="mt-4 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-[11px] text-slate-400 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-right px-4 py-3">Bonus NAV</th>
                <th className="text-right px-4 py-3">Bonus Units</th>
                <th className="text-right px-4 py-3">Bonus Capital</th>
                <th className="text-right px-4 py-3">Current Value</th>
                <th className="text-right px-4 py-3">Gain</th>
                {isAdmin && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="text-sm text-slate-500 py-12 text-center">
                    No bonus issuances yet.
                    {isAdmin && (
                      <span className="block mt-1 text-[11px]">
                        Go to <span className="text-amber-400">Admin → New Transaction → Bonus</span> to issue bonus units.
                      </span>
                    )}
                  </td>
                </tr>
              )}
              {filtered.map((c) => {
                const curVal = (c.unitsCr || 0) * currentNAV;
                const gain = curVal - (c.fundsCr || 0);
                const gainPos = gain >= 0;
                return (
                  <tr key={c.id} className="hover:bg-slate-800/50 transition-colors" data-testid={`bonus-row-${c.id}`}>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{longDate(c.date)}</td>
                    <td className="px-4 py-3 text-white font-medium truncate max-w-[180px]">
                      <div className="flex items-center gap-2">
                        <Sparkles size={12} className="text-amber-400 shrink-0" />
                        {c.name}
                      </div>
                      {c.details && (
                        <div className="text-[10px] text-slate-500 mt-0.5 truncate">{c.details}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-400 font-mono text-xs">₹{c.nav.toFixed(4)}</td>
                    <td className="px-4 py-3 text-right text-slate-300 font-mono text-xs">{fmtUnits(c.unitsCr || 0)}</td>
                    <td className="px-4 py-3 text-right text-amber-400 font-semibold">
                      {fmtINR(c.fundsCr || 0)}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-medium">
                      {fmtINR(curVal)}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium text-xs ${gainPos ? "text-emerald-400" : "text-rose-400"}`}>
                      {fmtINR(gain)}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end items-center gap-1">
                          <button
                            onClick={() => startEdit(c)}
                            data-testid={`bonus-edit-${c.id}`}
                            className="text-slate-500 hover:text-amber-400 p-1.5"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setDeleting(c)}
                            data-testid={`bonus-delete-${c.id}`}
                            className="text-slate-500 hover:text-rose-400 p-1.5"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-800">
          {filtered.length === 0 && (
            <div className="text-sm text-slate-500 py-8 text-center">
              No bonus issuances yet.
            </div>
          )}
          {filtered.map((c) => {
            const curVal = (c.unitsCr || 0) * currentNAV;
            const gain = curVal - (c.fundsCr || 0);
            return (
              <div key={c.id} className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border bg-amber-500/10 border-amber-500/40 text-amber-400">
                  <Sparkles size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {longDate(c.date)} · NAV ₹{c.nav.toFixed(4)}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {fmtUnits(c.unitsCr || 0)} units · current {fmtINR(curVal)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-amber-400">
                    {fmtINR(c.fundsCr || 0)}
                  </div>
                  <div className={`text-[11px] ${gain >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {gain >= 0 ? "+" : ""}{fmtINR(gain)}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => startEdit(c)} className="text-slate-500 p-1">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => setDeleting(c)} className="text-slate-500 p-1">
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={16} className="text-amber-400" />
              Edit Bonus
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                Member: <span className="text-white">{editing.memberName}</span>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-2.5 text-[11px] text-amber-300">
                Bonus is free units — does not affect Capital Invested or Avg NAV. To convert this entry to a Subscription or Redemption, edit the type from Cashflows or Admin.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Date</Label>
                  <Input type="date" value={editing.date} onChange={(e) => updateEdit("date", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Bonus NAV ₹</Label>
                  <Input type="number" step="0.0001" value={editing.nav} onChange={(e) => updateEdit("nav", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Bonus Capital ₹</Label>
                  <Input type="number" step="0.01" value={editing.funds} onChange={(e) => updateEdit("funds", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Bonus Units</Label>
                  <Input type="number" step="0.0001" value={editing.units} onChange={(e) => updateEdit("units", e.target.value)} className="mt-1.5 bg-slate-950 border-slate-800 text-white font-mono" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Details</Label>
                <Textarea value={editing.details} onChange={(e) => updateEdit("details", e.target.value)} rows={2} className="mt-1.5 bg-slate-950 border-slate-800 text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-slate-400 hover:text-white hover:bg-slate-800">
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="bonus-save-edit" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete bonus issuance?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will delete the bonus of <span className="text-white">{fmtINR(deleting?.fundsCr || 0)}</span>
              {" "}({fmtUnits(deleting?.unitsCr || 0)} units) for <span className="text-white">{deleting?.name}</span> on <span className="text-white">{deleting && longDate(deleting.date)}</span>. The member's bonus balance and overall holdings will be recomputed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="bonus-confirm-delete"
              className="bg-rose-500 hover:bg-rose-400 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
